import { Calculator } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { fromCents } from '@ankor-io/common/domain/pay/currency'
import type { CharterStatsPayload, CharterSummary as CharterSummaryType } from '@ankor-io/common/types/charter'
import { formatCurrency } from '@ankor-io/common/util/currencies'

import { Label } from '@/ui-components/Fieldset'
import { Heading } from '@/ui-components/Heading'
import { Input } from '@/ui-components/Input'
import { Radio, RadioField, RadioGroup } from '@/ui-components/Radio'

type GratuityCalculatorProps = {
  charter: CharterSummaryType | null
  stats: CharterStatsPayload | null
  onChange?: (amountCents: number) => void
}

const GratuityCalculator = ({ charter, stats, onChange }: GratuityCalculatorProps) => {
  const charterFee = charter?.charterFeeCents ?? 0
  const currency = charter?.currencyCode ?? 'USD'

  const gratuity10 = Math.round((Number(charterFee) * 10) / 100)
  const gratuity15 = Math.round((Number(charterFee) * 15) / 100)
  const gratuity20 = Math.round((Number(charterFee) * 20) / 100)
  const remainingBalance = stats?.apaRemainingCents ?? 0

  type OptionKey = 'none' | 'pct10' | 'pct15' | 'pct20' | 'remaining' | 'custom'
  const [selected, setSelected] = useState<OptionKey | null>(null)
  const [customAmount, setCustomAmount] = useState<string>('')

  const amountForOption = useMemo((): Record<OptionKey, number> => {
    return {
      none: 0,
      pct10: gratuity10,
      pct15: gratuity15,
      pct20: gratuity20,
      remaining: remainingBalance,
      custom: 0,
    }
  }, [gratuity10, gratuity15, gratuity20, remainingBalance])

  const customAmountCents = useMemo(() => {
    const parsed = Number.parseFloat(customAmount)
    if (Number.isNaN(parsed)) {
      return 0
    }

    return Math.round(parsed * 100)
  }, [customAmount])

  const isCustomValid = (selected !== 'custom' || customAmountCents > 0) && selected !== null

  useEffect(() => {
    if (!onChange || selected === null) {
      return
    }

    const amountCents = selected === 'custom' ? customAmountCents : amountForOption[selected]
    onChange(amountCents)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Calculator className="size-5" />
        <Heading level={3}>Gratuity Calculator</Heading>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400">
        Based on your charter fee of{' '}
        <span className="font-semibold">{formatCurrency(fromCents(charterFee), currency)}</span>, here are the
        recommended gratuity amounts in {currency}:
      </p>

      <RadioGroup
        value={selected ?? ''}
        onChange={(v: OptionKey) => setSelected(v)}
        className="space-y-3"
      >
        <RadioField className="items-center rounded-lg border px-4 py-3 border-gray-300 dark:border-gray-700">
          <Radio
            value="pct10"
            id="gratuity-pct10"
          />
          <Label
            htmlFor="gratuity-pct10"
            className="flex w-full items-center justify-between text-sm cursor-pointer"
            onClick={() => setSelected('pct10')}
          >
            <span>10% of Charter Fee</span>
            <span>{formatCurrency(fromCents(gratuity10), currency)}</span>
          </Label>
        </RadioField>

        <RadioField className="items-center rounded-lg border px-4 py-3 border-gray-300 dark:border-gray-700">
          <Radio
            value="pct15"
            id="gratuity-pct15"
          />
          <Label
            htmlFor="gratuity-pct15"
            className="flex w-full items-center justify-between text-sm cursor-pointer"
            onClick={() => setSelected('pct15')}
          >
            <span>15% of Charter Fee</span>
            <span>{formatCurrency(fromCents(gratuity15), currency)}</span>
          </Label>
        </RadioField>

        <RadioField className="items-center rounded-lg border px-4 py-3 border-gray-300 dark:border-gray-700">
          <Radio
            value="pct20"
            id="gratuity-pct20"
          />
          <Label
            htmlFor="gratuity-pct20"
            className="flex w-full items-center justify-between text-sm cursor-pointer"
            onClick={() => setSelected('pct20')}
          >
            <span>20% of Charter Fee</span>
            <span>{formatCurrency(fromCents(gratuity20), currency)}</span>
          </Label>
        </RadioField>

        <RadioField className="items-center rounded-lg border px-4 py-3 border-gray-300 dark:border-gray-700">
          <Radio
            value="remaining"
            id="gratuity-remaining"
          />
          <Label
            htmlFor="gratuity-remaining"
            className="flex w-full items-center justify-between text-sm cursor-pointer"
            onClick={() => setSelected('remaining')}
          >
            <span>Remaining Charter Balance</span>
            <span>{formatCurrency(fromCents(remainingBalance), currency)}</span>
          </Label>
        </RadioField>

        <RadioField className="items-center rounded-lg border px-4 py-3 border-gray-300 dark:border-gray-700">
          <Radio
            value="custom"
            id="gratuity-custom"
          />
          <Label
            htmlFor="gratuity-custom"
            className="flex w-full items-center justify-between gap-4 text-sm cursor-pointer"
            onClick={() => setSelected('custom')}
          >
            <span>Custom Amount</span>
            <div className="flex">
              <Input
                className="w-40"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                onClick={(e) => {
                  e.stopPropagation()
                  setSelected('custom')
                }}
                data-invalid={selected === 'custom' && !isCustomValid ? true : undefined}
                disabled={selected !== 'custom'}
              />
            </div>
          </Label>
        </RadioField>
        {selected === 'custom' && !isCustomValid && (
          <p className="text-sm text-red-600 dark:text-red-500">Amount must be greater than 0.</p>
        )}

        <RadioField className="items-center rounded-lg border px-4 py-3 border-gray-300 dark:border-gray-700">
          <Radio
            value="none"
            id="gratuity-none"
          />
          <Label
            htmlFor="gratuity-none"
            className="flex w-full items-center justify-between text-sm cursor-pointer"
            onClick={() => setSelected('none')}
          >
            <span>No additional gratuity</span>
            <span>{formatCurrency(fromCents(0), currency)}</span>
          </Label>
        </RadioField>
      </RadioGroup>
    </div>
  )
}

export default GratuityCalculator
